import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './sintMargrietPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function sintMargrietReading(landmark: Landmark) {
  if (landmark.id !== 'landmark-sint-margriet')
    throw new Error(`No Margriet plan for ${landmark.id}`);
  return reading;
}

export function sintMargrietFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error(`${landmark.id} lacks its component anchor`);
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const sintMargrietChapel = (landmark: Landmark) => ({
  ...sintMargrietReading(landmark).chapel,
  apse: 'flat' as const,
});

export function sintMargrietChapelFrame(landmark: Landmark): PlanFrame {
  return sintMargrietRangeFrame(sintMargrietFrame(landmark), sintMargrietReading(landmark).chapel);
}

export function sintMargrietRangeFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

/** Grounded bodies only: neither court nor canal entry becomes a precinct slab. */
export function sintMargrietFoundations(landmark: Landmark): Ring[] {
  const plan = sintMargrietFrame(landmark);
  return [
    vesselOutline(sintMargrietChapel(landmark)).map((p) =>
      toLayout(sintMargrietChapelFrame(landmark), p)
    ),
    ...sintMargrietReading(landmark).ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = sintMargrietRangeFrame(plan, part),
        centre = 'passageU' in part ? part.passageU : 0;
      return (
        part.passage
          ? [
              { ...rect, u1: centre - part.passage / 2 },
              { ...rect, u0: centre + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function sintMargrietObstacles(landmark: Landmark): Ring[] {
  const plan = sintMargrietFrame(landmark);
  return [
    ...sintMargrietFoundations(landmark),
    ...conventEnclosureRings(sintMargrietReading(landmark).walls).map((r) =>
      r.map((p) => toLayout(plan, p))
    ),
  ];
}

export function sintMargrietFloors(landmark: Landmark) {
  const plan = sintMargrietFrame(landmark),
    reading = sintMargrietReading(landmark);
  return [
    ...reading.floors.map((f) => ({ ...f, ring: f.ring.map((p) => [p[0]!, p[1]!] as const) })),
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => {
        const centre = 'passageU' in p ? p.passageU : 0;
        return {
          ring: corners({
            u0: centre - p.passage / 2,
            u1: centre + p.passage / 2,
            v0: -p.width / 2,
            v1: p.width / 2,
          }).map((q) =>
            toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
          ),
          height: 0.025,
          garden: false,
        };
      }),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
